/**
 * Fancy letters — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_lettering`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const fancyLettersPack: ToilePack = {
    slug: 'fancy-letters',
    category: 'illustration',
    name: 'Fancy letters',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'swash', label: 'Swash capitals', subject: 'a chart of the 26 capitals "A" to "Z" in an ornate swash script in six rows, each with a long curling entry stroke and a flourish below'},
        {key: 'filigree', label: 'Filigree', subject: 'a chart of decorative capitals "A" to "Z" each sitting in a small square of filigree scrollwork, five rows on the page'},
        {key: 'monogram', label: 'Monogram', subject: 'a single ornate capital "M" large in the center of the page with interlaced flourishes and a thin oval frame around it'},
    ],
    styles: [
        {key: 'swash', label: 'Swashes', style: 'fine monoline capitals with long curling swashes and flourishes', palette: 'black ink only', framing: 'centered on a white sheet with a generous margin'},
        {key: 'filigree', label: 'Filigree', style: 'ornate capitals filled with fine filigree scrollwork and tiny leaves', palette: 'black ink only', framing: 'centered on a white sheet with a generous margin'},
        {key: 'floral', label: 'Vines and leaves', style: 'capitals wound with a thin vine, small leaves and buds, drawn in clean outline', palette: 'black ink only', framing: 'centered on a white sheet with a generous margin'},
    ],
    directions: ['a heart', 'a crown', 'butterflies'],
    directionHint: 'try: a heart',
    seo: {
        title: 'Fancy Letters: Swash, Filigree, Monogram',
        metaDescription: 'Nine fancy letter sheets: ornate capitals with swashes, filigree and vines, drawn as clean black line for cards and monograms.',
        h1: 'Fancy letters: swash capitals, filigree, a monogram',
        tagline: 'Ornate capitals in three decoration styles, drawn as clean black line.',
        intro: 'Fancy letters are decorated capitals for a card, a scrapbook title, a monogram or a wedding sign. The nine sheets show ornate swash capitals, capitals set in filigree scrollwork, and a single large M with interlaced flourishes. Each decoration comes as swashes, filigree, or wound vines and leaves, in clean black line. Ask for a heart, a crown or butterflies.',
        useCases: [
            'a wedding sign or place card',
            'a scrapbook page title',
            'a monogram for a card or a gift',
            'a framed initial for a wall',
        ],
        faq: [
            {q: 'Are the alphabets complete?', a: 'The swash and filigree sheets each chart all 26 capitals from A to Z. The monogram sheet shows a single M.'},
            {q: 'What is the difference between the three styles?', a: 'Swashes are fine monoline capitals with long curling strokes. Filigree fills the capitals with fine scrollwork and tiny leaves. Vines and leaves wind a thin vine with small leaves and buds around each capital.'},
            {q: 'Can I get a different motif?', a: 'Yes. Ask for a heart, a crown or butterflies, and the capitals are drawn with it.'},
            {q: 'Are these in color?', a: 'No. They are drawn as clean black line on white.'},
        ],
    },
}
